// Thin loader around pdfjs-dist 1.10 (the version pinned in this repo). We use
// the "combined" build (worker bundled inline) and run on the main thread so we
// don't have to ship a separate worker file through CRA. Document PDFs are
// small, so main-thread render is fine.

let pdfjsPromise = null;

const loadPdfjs = () => {
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = import('pdfjs-dist/build/pdf.combined').then((mod) => {
    const PDFJS = mod.PDFJS || mod.default || mod || window.PDFJS;
    if (PDFJS) {
      PDFJS.disableWorker = true;
      // Never compile font code with eval (Nic 2026-10-24): pdf.js before
      // 4.2.67 can run script hidden in a crafted font (CVE-2024-4367) when
      // this is on. Fonts still render through the interpreter, a little
      // slower. The real fix is upgrading pdf.js (a post-launch project).
      PDFJS.isEvalSupported = false;
    }
    return PDFJS;
  });
  return pdfjsPromise;
};

// Resolve the promise-ish return shapes across pdfjs versions.
const settle = (taskOrPromise) =>
  taskOrPromise && taskOrPromise.promise
    ? taskOrPromise.promise
    : Promise.resolve(taskOrPromise);

// Fetch a PDF (by URL) and return the loaded document + page count.
export const loadPdfDocument = async (url) => {
  const PDFJS = await loadPdfjs();
  const res = await fetch(url);
  // Surface a real error instead of letting pdfjs choke on an error body —
  // presigned URLs expire, and S3 answers those with an XML error document.
  if (!res.ok) {
    throw new Error(
      `Could not load the PDF (HTTP ${res.status}). The link may have expired — try reloading the page.`,
    );
  }
  const data = await res.arrayBuffer();
  const pdf = await settle(PDFJS.getDocument({ data }));
  return { pdf, numPages: pdf.numPages };
};

// Render a single (1-indexed) page into a canvas at a given CSS width.
// Returns the rendered { width, height } so callers can size the overlay.
export const renderPageToCanvas = async (pdf, pageNumber, canvas, cssWidth) => {
  const page = await settle(pdf.getPage(pageNumber));
  const baseViewport = page.getViewport(1);
  const scale = cssWidth / baseViewport.width;
  const viewport = page.getViewport(scale);
  // The canvas backing store is drawn at the screen's pixel density and shown
  // at CSS size; at 1x a phone (DPR 3) upscales it and the text goes blurry
  // (Nic 2026-09-23). iOS caps a canvas at ~16.7M pixels, so the density
  // backs off for very large renders.
  const MAX_CANVAS_PIXELS = 16000000;
  let dpr = Math.min(
    (typeof window !== 'undefined' && window.devicePixelRatio) || 1,
    3,
  );
  const cssArea = viewport.width * viewport.height;
  if (cssArea * dpr * dpr > MAX_CANVAS_PIXELS) {
    dpr = Math.max(1, Math.sqrt(MAX_CANVAS_PIXELS / cssArea));
  }
  const renderViewport = page.getViewport(scale * dpr);
  const ctx = canvas.getContext('2d');
  canvas.width = Math.floor(renderViewport.width);
  canvas.height = Math.floor(renderViewport.height);
  canvas.style.width = `${viewport.width}px`;
  canvas.style.height = `${viewport.height}px`;
  await settle(page.render({ canvasContext: ctx, viewport: renderViewport }));
  // `scale` = rendered CSS px per PDF point. Callers use it to size text overlays
  // (whose font sizes are stored in PDF points) to match the burned PDF.
  return { width: viewport.width, height: viewport.height, scale };
};

// The signer's page render (Nic 2026-10-15: pinch-zoom on a phone blurred
// the page). Draws the page at the screen's density TIMES `oversample`, so a
// 2x pinch stays crisp, into one throwaway canvas, and hands back an image
// URL: an <img> per page instead of a live canvas per page, which iOS keeps
// within memory on a long agreement. Same 16M-pixel cap as above. The caller
// revokes the URL when it re-renders or unmounts.
// maxPixels (Nic 2026-10-24): the caller's per-page share of a whole
// packet's memory budget, so a long agreement can't run an iPhone out of
// memory (Safari keeps a decoded bitmap for every page image).
export const renderPageToImageUrl = async (
  pdf,
  pageNumber,
  cssWidth,
  { oversample = 1, maxPixels = 16000000 } = {},
) => {
  const page = await settle(pdf.getPage(pageNumber));
  const baseViewport = page.getViewport(1);
  const scale = cssWidth / baseViewport.width;
  const viewport = page.getViewport(scale);
  const MAX_CANVAS_PIXELS = Math.min(16000000, Math.max(1000000, maxPixels));
  const dpr = Math.min((typeof window !== 'undefined' && window.devicePixelRatio) || 1, 3);
  let density = dpr * Math.max(1, oversample);
  const cssArea = viewport.width * viewport.height;
  if (cssArea * density * density > MAX_CANVAS_PIXELS) {
    density = Math.max(1, Math.sqrt(MAX_CANVAS_PIXELS / cssArea));
  }
  const renderViewport = page.getViewport(scale * density);
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(renderViewport.width);
  canvas.height = Math.floor(renderViewport.height);
  const ctx = canvas.getContext('2d');
  // White under the page: a transparent PNG shows the card color through.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  let blob;
  try {
    await settle(page.render({ canvasContext: ctx, viewport: renderViewport }));
    blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  } finally {
    // Free the bitmap now (and on a failed draw); only the encoded image is
    // kept.
    canvas.width = 0;
    canvas.height = 0;
  }
  if (!blob) throw new Error('Could not draw the page.');
  return {
    url: URL.createObjectURL(blob),
    width: viewport.width,
    height: viewport.height,
    scale,
  };
};
